import Phaser from "phaser";
import { COLORS, hexToPhaser } from "./colors";

export type NotificationType = "success" | "error" | "info" | "warning";

interface Notification {
  container: Phaser.GameObjects.Container;
  timer: Phaser.Time.TimerEvent;
}

export class NotificationHandler {
  private scene: Phaser.Scene;
  private uiLayer: Phaser.GameObjects.Container | null;
  private notifications: Notification[] = [];
  private readonly SPACING = 20;
  private readonly BOX_WIDTH = 260;
  private readonly BOX_PADDING = 20;

  constructor(scene: Phaser.Scene, uiLayer: Phaser.GameObjects.Container | null = null) {
    this.scene = scene;
    this.uiLayer = uiLayer;
  }

  /**
   * Show a notification message with the cosmetics-window style
   * Messages stack vertically with individual timers
   * @param message The message to display
   * @param type The type of notification (success, error, info, warning)
   * @param duration How long to show the notification in milliseconds (default 3000)
   * @param label Optional short label chip centered on the notification's top border
   */
  show(
    message: string,
    type: NotificationType = "info",
    duration: number = 3000,
    label?: string,
  ) {
    if (!this.scene) {
      console.warn(
        "Cannot show notification: scene or uiLayer is not available",
      );
      return;
    }
    const gameWidth = this.scene.scale.width;

    // Message box
    const boxWidth = Math.min(this.BOX_WIDTH, gameWidth - 64);

    // Create text first to measure height
    const text = this.scene.add
      .text(0, 0, message.toUpperCase(), {
        fontSize: "18px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
        wordWrap: { width: boxWidth - 40 },
      })
      .setOrigin(0.5, 0.5);
    text.setScrollFactor(0);

    // Calculate box height based on text height
    const textHeight = text.displayHeight;
    const boxHeight = textHeight + this.BOX_PADDING * 2;

    // Create graphics box
    const box = this.scene.add.graphics();

    // Set color based on type
    let boxColor: string;
    switch (type) {
      case "success":
        boxColor = COLORS.GREEN;
        break;
      case "error":
        boxColor = COLORS.RED;
        break;
      case "warning":
        boxColor = COLORS.ORANGE;
        break;
      case "info":
      default:
        boxColor = COLORS.FG;
        break;
    }

    box.fillStyle(hexToPhaser(boxColor), 1);
    box.fillRoundedRect(0, 0, boxWidth, boxHeight, 12);
    box.lineStyle(4, hexToPhaser(COLORS.WHITE), 0.8);
    box.strokeRoundedRect(0, 0, boxWidth, boxHeight, 12);
    box.setScrollFactor(0);

    // Position text in center of box
    text.setPosition(boxWidth / 2, boxHeight / 2);

    const children: Phaser.GameObjects.GameObject[] = [box, text];

    // Optional label chip, centered on the box's top border (half above, half below)
    let labelOverhang = 0;
    if (label) {
      const labelText = this.scene.add
        .text(0, 0, label.toUpperCase(), {
          fontSize: "11px",
          color: COLORS.WHITE,
          fontFamily: "Anton",
        })
        .setOrigin(0.5, 0.5);
      labelText.setScrollFactor(0);

      const labelPaddingX = 10;
      const labelHeight = labelText.displayHeight + 6;
      const labelWidth = labelText.displayWidth + labelPaddingX * 2;

      const labelBg = this.scene.add.graphics();
      labelBg.fillStyle(hexToPhaser(boxColor), 1);
      labelBg.fillRoundedRect(-labelWidth / 2, -labelHeight / 2, labelWidth, labelHeight, 6);
      labelBg.lineStyle(3, hexToPhaser(COLORS.WHITE), 0.9);
      labelBg.strokeRoundedRect(-labelWidth / 2, -labelHeight / 2, labelWidth, labelHeight, 6);
      labelBg.setScrollFactor(0);
      labelBg.setPosition(boxWidth / 2, 0);
      labelText.setPosition(boxWidth / 2, 0);

      children.push(labelBg, labelText);
      labelOverhang = labelHeight / 2;
    }

    // Create container for this notification — parent to uiLayer when available
    // so that in MainScene (dual-camera setup) it only renders on the UI camera.
    const container = this.scene.add.container(0, 0, children);
    container.setDepth(100004);
    container.setVisible(true);
    container.setData("boxHeight", boxHeight);
    container.setData("labelOverhang", labelOverhang);
    if (this.uiLayer) {
      this.uiLayer.add(container);
    }

    // Add to notifications array first
    this.notifications.push({ container, timer: null as any });

    // Position the notification in the stack
    this.updateNotificationPositions();

    // Create timer for this specific notification
    const timer = this.scene.time.delayedCall(duration, () => {
      this.removeNotification(container);
    });

    // Update timer in notifications array
    const notificationIndex = this.notifications.findIndex(
      (n) => n.container === container,
    );
    if (notificationIndex !== -1) {
      this.notifications[notificationIndex].timer = timer;
    }
  }

  /**
   * Update positions of all notifications to stack vertically
   */
  private updateNotificationPositions() {
    const gameWidth = this.scene.scale.width;
    const isBaseScene = this.scene.scene?.key === "BaseScene";
    const topPadding = isBaseScene ? 24 : 80;
    let yOffset = topPadding;

    // Update positions for all notifications
    this.notifications.forEach((notification) => {
      const container = notification.container;
      const boxHeight = (container.getData("boxHeight") as number) ?? 0;
      const labelOverhang = (container.getData("labelOverhang") as number) ?? 0;
      // Reserve room above the box so a label chip straddling the top border
      // doesn't overlap the notification stacked above it.
      yOffset += labelOverhang;
      container.setPosition(gameWidth / 2 - this.BOX_WIDTH / 2, yOffset);
      yOffset += boxHeight + this.SPACING;
    });
  }

  /**
   * Remove a specific notification
   */
  private removeNotification(container: Phaser.GameObjects.Container) {
    const index = this.notifications.findIndex(
      (n) => n.container === container,
    );
    if (index !== -1) {
      const notification = this.notifications[index];
      // Cancel the timer
      this.scene.time.removeEvent(notification.timer);
      // Remove the notification
      container.destroy();
      this.notifications.splice(index, 1);
      // Re-position remaining notifications
      this.updateNotificationPositions();
    }
  }

  /**
   * Hide all notifications
   */
  hideAll() {
    this.notifications.forEach((notification) => {
      this.scene.time.removeEvent(notification.timer);
      notification.container.destroy();
    });
    this.notifications = [];
  }

  /**
   * Check if any notifications are currently showing
   */
  isVisible(): boolean {
    return this.notifications.length > 0;
  }

  /**
   * Destroy the notification handler and all active notifications
   */
  destroy() {
    this.hideAll();
  }
}
